const API_BASE = 'https://api.wedmin.biz/photo-studio';

function getHeaders(): HeadersInit {
  const token = localStorage.getItem('accessToken');
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

async function request<T = any>(
  path: string,
  options?: RequestInit
): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: { ...getHeaders(), ...options?.headers },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    const msg = Array.isArray(errorData?.message) ? errorData.message.join(', ') : (errorData?.message || `HTTP ${response.status}`);
    throw new Error(msg);
  }

  const contentType = response.headers.get('content-type');
  if (contentType?.includes('application/json')) {
    const json = await response.json();
    return json.data ?? json;
  }
  return {} as T;
}

// --- 고객 API ---
export const customerApi = {
  list: (params: Record<string, any>) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') qs.append(k, String(v));
    });
    return request(`/customer?${qs}`);
  },
  create: (data: any) =>
    request('/customer', { method: 'POST', body: JSON.stringify(data) }),
  detail: (id: number, tab = 'OVERVIEW') =>
    request(`/customer/${id}/detail?tab=${tab}`),
  update: (id: number, data: any) =>
    request(`/customer/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: (id: number) =>
    request(`/customer/${id}`, { method: 'DELETE' }),

  importExcel: async (file: File) => {
    const token = localStorage.getItem('accessToken');
    const formData = new FormData();
    formData.append('file', file);
    const response = await fetch(`${API_BASE}/customer/import-excel`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: formData,
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      const msg = Array.isArray(errorData?.message) ? errorData.message.join(', ') : (errorData?.message || `HTTP ${response.status}`);
      throw new Error(msg);
    }
    const json = await response.json();
    return json.data ?? json;
  },

  // 상담메모
  memoList: (customerId: number) =>
    request(`/customer/${customerId}/memo`),
  memoCreate: (customerId: number, data: any) =>
    request(`/customer/${customerId}/memo`, { method: 'POST', body: JSON.stringify(data) }),
  memoUpdate: (id: number, data: any) =>
    request(`/customer/memo/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  memoDelete: (id: number) =>
    request(`/customer/memo/${id}`, { method: 'DELETE' }),
};

// --- 계약 API ---
export const contractApi = {
  list: (params: Record<string, any>) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') qs.append(k, String(v));
    });
    return request(`/contract?${qs}`);
  },
  create: (data: any) =>
    request('/contract', { method: 'POST', body: JSON.stringify(data) }),
  detail: (id: number) =>
    request(`/contract/${id}`),
  update: (id: number, data: any) =>
    request(`/contract/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: (id: number) =>
    request(`/contract/${id}`, { method: 'DELETE' }),

  // 품목
  addItem: (contractId: number, data: any) =>
    request(`/contract/${contractId}/item`, { method: 'POST', body: JSON.stringify(data) }),
  updateItem: (itemId: number, data: any) =>
    request(`/contract/item/${itemId}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteItem: (itemId: number) =>
    request(`/contract/item/${itemId}`, { method: 'DELETE' }),
};

// --- 일정 API ---
export const scheduleApi = {
  list: (params: Record<string, any>) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') qs.append(k, String(v));
    });
    return request(`/schedule?${qs}`);
  },
  create: (data: any) =>
    request('/schedule', { method: 'POST', body: JSON.stringify(data) }),
  detail: (id: number) =>
    request(`/schedule/${id}`),
  update: (id: number, data: any) =>
    request(`/schedule/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: (id: number) =>
    request(`/schedule/${id}`, { method: 'DELETE' }),
  selectRoomGrid: (date: string) => request(`/schedule/select-room-grid?date=${date}`),
  shootingGrid: (date: string) => request(`/schedule/shooting-grid?date=${date}`),
};

// --- 발주확인(역발주) API ---
export const orderConfirmationApi = {
  send: async (data: any) => request('/order-confirmation/send', { method: 'POST', body: JSON.stringify(data) }),
  previewUrl: (customerIdx: number) => `${API_BASE}/order-confirmation/preview/${customerIdx}`,
};

// --- 작업 API ---
export const workOrderApi = {
  list: (params: Record<string, any>) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') qs.append(k, String(v));
    });
    return request(`/work-order?${qs}`);
  },
  create: (data: any) =>
    request('/work-order', { method: 'POST', body: JSON.stringify(data) }),
  detail: (id: number) =>
    request(`/work-order/${id}`),
  update: (id: number, data: any) =>
    request(`/work-order/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  changeStage: (id: number, targetStage: string) =>
    request(`/work-order/${id}/stage`, { method: 'PATCH', body: JSON.stringify({ targetStage }) }),
  delete: (id: number) =>
    request(`/work-order/${id}`, { method: 'DELETE' }),
};

// --- 결제 API ---
export const paymentApi = {
  list: (params: Record<string, any>) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') qs.append(k, String(v));
    });
    return request(`/payment?${qs}`);
  },
  create: (data: any) =>
    request('/payment', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: number, data: any) =>
    request(`/payment/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: (id: number) =>
    request(`/payment/${id}`, { method: 'DELETE' }),

  // 환불
  refundList: (params: Record<string, any>) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') qs.append(k, String(v));
    });
    return request(`/payment/refund?${qs}`);
  },
  refundCreate: (data: any) =>
    request('/payment/refund', { method: 'POST', body: JSON.stringify(data) }),
};

// --- 입출고 API ---
export const inventoryApi = {
  workStatus: (params: Record<string, any>) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') qs.append(k, String(v));
    });
    return request(`/work-order/inventory?${qs}`);
  },
  confirmation: (params: Record<string, any>) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') qs.append(k, String(v));
    });
    return request(`/work-order/inventory/confirmation?${qs}`);
  },
  stageSummary: (itemCategory?: string) => {
    const qs = itemCategory ? `?itemCategory=${itemCategory}` : '';
    return request(`/work-order/inventory/stage-summary${qs}`);
  },
  confirm: (id: number) =>
    request(`/work-order/${id}/confirm`, { method: 'PATCH' }),
};

// --- 파트너 업체 API (공통) ---
export const partnerVendorApi = {
  list: (params: Record<string, any>) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') qs.append(k, String(v));
    });
    return fetch(`https://api.wedmin.biz/order/partner-vendors?${qs}`, { headers: getHeaders() })
      .then(res => res.json())
      .then(json => json.data ?? json);
  },
};

// --- 내 업체 정보 API ---
export const myVendorApi = {
  get: () =>
    fetch('https://api.wedmin.biz/vendor/me', { headers: getHeaders() })
      .then(res => res.json())
      .then(json => json.data ?? json),
};

// --- 상품 API (공통) ---
const PRODUCT_BASE = 'https://api.wedmin.biz/product';

async function productRequest<T = any>(path: string, options?: RequestInit): Promise<T> {
  const response = await fetch(`${PRODUCT_BASE}${path}`, {
    ...options,
    headers: { ...getHeaders(), ...options?.headers },
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    const msg = Array.isArray(errorData?.message) ? errorData.message.join(', ') : (errorData?.message || `HTTP ${response.status}`);
    throw new Error(msg);
  }
  const contentType = response.headers.get('content-type');
  if (contentType?.includes('application/json')) {
    const json = await response.json();
    return json.data ?? json;
  }
  return {} as T;
}

// --- 파일 업로드 API ---
export const fileApi = {
  getPresignedUrl: async (params: { mimetype: string; type: string; id: string; extension: string }) => {
    const qs = new URLSearchParams(params);
    const res = await fetch(`https://api.wedmin.biz/v1/file/presigned-url?${qs}`, { headers: getHeaders() });
    if (!res.ok) throw new Error('presigned URL 생성 실패');
    const json = await res.json();
    return json.data ?? json;
  },
  upload: async (file: File, type: string, id: string): Promise<string> => {
    const ext = file.name.split('.').pop() || 'jpg';
    const { url } = await fileApi.getPresignedUrl({ mimetype: file.type, type, id, extension: ext });
    await fetch(url, { method: 'PUT', body: file, headers: { 'Content-Type': file.type } });
    return url.split('?')[0];
  },
};

export const productApi = {
  list: (params: Record<string, any>) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') qs.append(k, String(v));
    });
    return productRequest(`?${qs}`);
  },
  detail: (id: number) => productRequest(`/${id}`),
  create: (data: any) =>
    productRequest('', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: number, data: any) =>
    productRequest(`/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: (id: number) =>
    productRequest(`/${id}`, { method: 'DELETE' }),
};

// --- 정산 API ---
export const settlementApi = {
  list: (params: Record<string, any>) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') qs.append(k, String(v));
    });
    return request(`/settlement?${qs}`);
  },
  create: (data: any) =>
    request('/settlement', { method: 'POST', body: JSON.stringify(data) }),
  detail: (id: number) =>
    request(`/settlement/${id}`),
  update: (id: number, data: any) =>
    request(`/settlement/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),

  customerPayments: (params: Record<string, any>) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') qs.append(k, String(v));
    });
    return request(`/settlement/customer-payments?${qs}`);
  },

  partnerSummary: (params: Record<string, any>) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') qs.append(k, String(v));
    });
    return request(`/settlement/partner-summary?${qs}`);
  },

  sendInvoice: (idx: number, data?: any) =>
    request(`/settlement/${idx}/send-invoice`, { method: 'POST', body: data ? JSON.stringify(data) : undefined }),

  invoicePdfUrl: (idx: number) =>
    `${API_BASE}/settlement/${idx}/invoice/download`,
};

export const depositApi = {
  list: (params: Record<string, any>) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') qs.append(k, String(v));
    });
    return request(`/settlement/deposit?${qs}`);
  },
  create: (data: any) =>
    request('/settlement/deposit', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: number, data: any) =>
    request(`/settlement/deposit/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: (id: number) =>
    request(`/settlement/deposit/${id}`, { method: 'DELETE' }),
};

export const purchaseApi = {
  list: (params: Record<string, any>) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') qs.append(k, String(v));
    });
    return request(`/settlement/purchase?${qs}`);
  },
  create: (data: any) =>
    request('/settlement/purchase', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: number, data: any) =>
    request(`/settlement/purchase/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: (id: number) =>
    request(`/settlement/purchase/${id}`, { method: 'DELETE' }),
};

export const generalTransactionApi = {
  list: (params: Record<string, any>) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') qs.append(k, String(v));
    });
    return request(`/settlement/transaction?${qs}`);
  },
  summary: (params: Record<string, any>) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') qs.append(k, String(v));
    });
    return request(`/settlement/transaction/summary?${qs}`);
  },
  create: (data: any) =>
    request('/settlement/transaction', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: number, data: any) =>
    request(`/settlement/transaction/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: (id: number) =>
    request(`/settlement/transaction/${id}`, { method: 'DELETE' }),
};

export const transactionCategoryApi = {
  list: (params?: Record<string, any>) => {
    const qs = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== '') qs.append(k, String(v));
      });
    }
    return request(`/settlement/category?${qs}`);
  },
  create: (data: any) =>
    request('/settlement/category', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: number, data: any) =>
    request(`/settlement/category/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: (id: number) =>
    request(`/settlement/category/${id}`, { method: 'DELETE' }),
};

export const invoiceHistoryApi = {
  list: (params: Record<string, any>) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') qs.append(k, String(v));
    });
    return request(`/settlement/invoice-history?${qs}`);
  },
};

// --- 촬영 컷수/원본 관리 API ---
export const shootingRecordApi = {
  list: (params: Record<string, any>) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') qs.append(k, String(v));
    });
    return request(`/shooting-record?${qs}`);
  },
  detail: (id: number) => request(`/shooting-record/${id}`),
  create: (data: any) =>
    request('/shooting-record', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: number, data: any) =>
    request(`/shooting-record/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: (id: number) =>
    request(`/shooting-record/${id}`, { method: 'DELETE' }),
};

// --- 납품 확인 API ---
export const deliveryApi = {
  list: (params: Record<string, any>) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') qs.append(k, String(v));
    });
    return request(`/delivery?${qs}`);
  },
  detail: (id: number) => request(`/delivery/${id}`),
  create: (data: any) =>
    request('/delivery', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: number, data: any) =>
    request(`/delivery/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: (id: number) =>
    request(`/delivery/${id}`, { method: 'DELETE' }),
  confirm: (id: number) =>
    request(`/delivery/${id}/confirm`, { method: 'PATCH' }),
};

// --- 문자 발송 API ---
export const smsApi = {
  send: async (data: any) => request('/sms/send', { method: 'POST', body: JSON.stringify(data) }),
  sendBulk: async (data: any) => request('/sms/bulk', { method: 'POST', body: JSON.stringify(data) }),
};

// --- 문자 템플릿 API ---
export const smsTemplateApi = {
  list: async (params?: any) => {
    const qs = new URLSearchParams(params || {}).toString();
    return request(`/sms-template?${qs}`);
  },
  detail: async (id: number) => request(`/sms-template/${id}`),
  create: async (data: any) => request('/sms-template', { method: 'POST', body: JSON.stringify(data) }),
  update: async (id: number, data: any) => request(`/sms-template/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: async (id: number) => request(`/sms-template/${id}`, { method: 'DELETE' }),
};

// --- 알림 발송 기록 API ---
export const notificationLogApi = {
  list: (params: Record<string, any>) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') qs.append(k, String(v));
    });
    return request(`/notification-log?${qs}`);
  },
  detail: (id: number) => request(`/notification-log/${id}`),
  create: (data: any) =>
    request('/notification-log', { method: 'POST', body: JSON.stringify(data) }),
  delete: (id: number) =>
    request(`/notification-log/${id}`, { method: 'DELETE' }),
};
